import React, { useState, useEffect } from 'react';
import { getDatabase, ref, set, onValue, remove } from 'firebase/database';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Delete, Download, Eye, Edit } from 'react-feather';
import { Modal, Button, ConfigProvider, Popconfirm, message } from 'antd';
import QRCode from 'qrcode';

// Import images
import edaplogo from "./img/edaplogo.png";
import unilislogo from "./img/unilislogo.png";
import rectorSig from "./img/rector_sig.png";
import './certificatscolarite.css';

const CertificatScolariteTemplate = ({ app }) => {
    const [formData, setFormData] = useState({
        noms: '',
        dateNaissance: '',
        lieuNaissance: '',
        prepositionLieuNaissance: 'à',
        pays: '',
        numeroEnregistrement: '',
        anneeAcademique: '',
        sujetRecherche: '',
        dateEmission: new Date().toISOString().split('T')[0]
    });
    const [documents, setDocuments] = useState([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [qrCodeData, setQrCodeData] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const db = getDatabase(app);

    // Load documents from Firebase
    useEffect(() => {
        const documentsRef = ref(db, `certificatscolarite/UNCR/`);
        onValue(documentsRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                const documentArray = Object.keys(data).map((key) => ({
                    id: key,
                    ...data[key],
                }));
                setDocuments(documentArray);
            }
        });
    }, [db]);

    // Generate QR Code
    useEffect(() => {
        const generateQRCode = async () => {
            if (!selectedDocument) return;

            let qrData = `CERTIFICAT DE SCOLARITE - UNILIS\n`;
            Object.keys(selectedDocument).forEach(key => {
                if (key !== 'createdAt' && selectedDocument[key]) {
                    qrData += `${key}: ${selectedDocument[key]}\n`;
                }
            });
            qrData += `Status: Document Verified\nIssued by: UNILIS - Université de Lisala`;

            try {
                const qrCodeUrl = await QRCode.toDataURL(qrData, {
                    width: 120,
                    margin: 1,
                    color: { dark: '#000000', light: '#FFFFFF' }
                });
                setQrCodeData(qrCodeUrl);
            } catch (error) {
                console.error('Error generating QR code:', error);
            }
        };
        if (selectedDocument) generateQRCode();
    }, [selectedDocument]);

    // Handle ESC key press to close modal
    useEffect(() => {
        const handleEsc = (event) => {
            if (event.key === 'Escape' && modalVisible) {
                handleModalClose();
            }
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [modalVisible]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        // Validate required fields
        const requiredFields = ['noms', 'dateNaissance', 'lieuNaissance', 'pays', 'numeroEnregistrement', 'anneeAcademique', 'sujetRecherche', 'dateEmission'];
        for (const field of requiredFields) {
            if (!formData[field] || formData[field].trim() === '') {
                alert(`${field} est obligatoire!`);
                return;
            }
        }

        if (isEditing && editingId) {
            // Update existing document
            const documentRefPath = ref(db, `certificatscolarite/UNCR/${editingId}`);
            set(documentRefPath, {
                ...formData,
                createdAt: documents.find(doc => doc.id === editingId)?.createdAt || new Date().toISOString()
            })
                .then(() => {
                    console.log('Certificat de Scolarité document updated successfully');
                    message.success('Document modifié avec succès');
                    resetForm();
                })
                .catch((error) => {
                    console.error('Error updating Certificat de Scolarité document:', error);
                    message.error('Erreur lors de la modification');
                });
        } else {
            // Create new document
            const sanitizedName = formData.noms.replace(/[.#$[\]]/g, '_').replace(/\s+/g, '_');
            const documentRefPath = ref(db, `certificatscolarite/UNCR/${sanitizedName}_${Date.now()}`);
            set(documentRefPath, {
                ...formData,
                createdAt: new Date().toISOString()
            })
                .then(() => {
                    console.log('Certificat de Scolarité document saved successfully');
                    message.success('Document créé avec succès');
                    resetForm();
                })
                .catch((error) => {
                    console.error('Error saving Certificat de Scolarité document:', error);
                    message.error('Erreur lors de la création');
                });
        }
    };

    const resetForm = () => {
        setFormData({
            noms: '',
            dateNaissance: '',
            lieuNaissance: '',
            prepositionLieuNaissance: 'à',
            pays: '',
            numeroEnregistrement: '',
            anneeAcademique: '',
            sujetRecherche: '',
            dateEmission: new Date().toISOString().split('T')[0]
        });
        setIsEditing(false);
        setEditingId(null);
    };

    const handleEditDocument = (document) => {
        setFormData({
            noms: document.noms || '',
            dateNaissance: document.dateNaissance || '',
            lieuNaissance: document.lieuNaissance || '',
            prepositionLieuNaissance: document.prepositionLieuNaissance || 'à',
            pays: document.pays || '',
            numeroEnregistrement: document.numeroEnregistrement || '',
            anneeAcademique: document.anneeAcademique || '',
            sujetRecherche: document.sujetRecherche || '',
            dateEmission: document.dateEmission || new Date().toISOString().split('T')[0]
        });
        setIsEditing(true);
        setEditingId(document.id);
        
        // Scroll to form
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const showDocumentModal = (document) => {
        setSelectedDocument(document);
        setModalVisible(true);
    };

    const handleModalClose = () => {
        setModalVisible(false);
        setSelectedDocument(null);
    };

    const handleDeleteDocument = (id) => {
        remove(ref(db, `certificatscolarite/UNCR/${id}`))
            .then(() => {
                message.success('Document supprimé avec succès');
            })
            .catch((error) => {
                console.error('Error deleting document:', error);
                message.error('Erreur lors de la suppression');
            });
    };

    const formatDateWithMonthName = (dateStr) => {
        const date = new Date(dateStr);
        const months = [
            "janvier", "février", "mars", "avril", "mai", "juin",
            "juillet", "août", "septembre", "octobre", "novembre", "décembre"
        ];
        const day = date.getDate();
        const month = months[date.getMonth()];
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
    };

    const formatDateDDMMYYYY = (dateStr) => {
        const date = new Date(dateStr);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const downloadPDF = async () => {
        if (!selectedDocument) return;

        const element = document.getElementById('certificat-scolarite-document');
        if (!element) {
            console.error('Document element not found');
            return;
        }

        const clone = element.cloneNode(true);
        clone.style.cssText = `
            width: 21cm !important;
            height: auto !important;
            min-height: 29.7cm !important;
            margin: 0 !important;
            padding: 2cm 0.5cm !important;
            background: white !important;
            position: relative !important;
            box-sizing: border-box !important;
            font-family: 'Times New Roman', serif !important;
            overflow: visible !important;
        `;

        const tempContainer = document.createElement('div');
        tempContainer.style.cssText = `
            position: fixed;
            top: -9999px;
            left: -9999px;
            z-index: -1;
            width: 21cm;
            height: auto;
            min-height: 29.7cm;
        `;
        tempContainer.appendChild(clone);
        document.body.appendChild(tempContainer);

        try {
            // Wait for images to load
            const images = clone.querySelectorAll('img');
            await Promise.all(Array.from(images).map(img => {
                return new Promise((resolve) => {
                    if (img.complete) {
                        resolve();
                    } else {
                        img.onload = resolve;
                        img.onerror = resolve;
                    }
                });
            }));

            // Small delay to ensure rendering is complete
            await new Promise(resolve => setTimeout(resolve, 500));

            const canvas = await html2canvas(clone, {
                scale: 2,
                useCORS: true,
                allowTaint: true,
                backgroundColor: '#FFFFFF',
                logging: false,
                imageTimeout: 15000,
                scrollX: 0,
                scrollY: 0,
                width: clone.scrollWidth,
                height: clone.scrollHeight
            });
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF('p', 'pt', 'a4'); // 'p' for portrait
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();

            // Calculate the aspect ratio to fit the image properly
            const imgWidth = canvas.width;
            const imgHeight = canvas.height;
            const ratio = Math.min(pageWidth / imgWidth, pageHeight / imgHeight);
            const scaledWidth = imgWidth * ratio;
            const scaledHeight = imgHeight * ratio;
            // Center the image on the page
            const x = (pageWidth - scaledWidth) / 2;
            const y = (pageHeight - scaledHeight) / 2;
            pdf.addImage(imgData, 'PNG', x, y, scaledWidth, scaledHeight);
            pdf.save(`${selectedDocument.noms?.replace(/\s+/g, '_') || 'document'}_CERTIFICAT_SCOLARITE.pdf`);
        } catch (error) {
            console.error('Error generating PDF:', error);
            message.error('Erreur lors de la génération du PDF');
        } finally {
            document.body.removeChild(tempContainer);
        }
    };

    return (
        <div className="certificat-scolarite-container">
            <div className="certificat-scolarite-form-section">
                <div style={{ textAlign: 'center' }}>
                    <img src={edaplogo} alt="Logo EDAP" className="certificat-scolarite-form-logo" />
                    <h2 style={{ textTransform: 'uppercase' }}>Certificat de Scolarité</h2>
                </div>

                <form onSubmit={handleSubmit} className="certificat-scolarite-form">
                    <div className="certificat-scolarite-form-row">
                        <input
                            type="text"
                            name="noms"
                            value={formData.noms}
                            onChange={handleInputChange}
                            placeholder="Nom complet de l'étudiant"
                            required
                            className="certificat-scolarite-input"
                        />
                        <input
                            type="date"
                            name="dateNaissance"
                            value={formData.dateNaissance}
                            onChange={handleInputChange}
                            required
                            className="certificat-scolarite-input"
                        />
                    </div>

                    <div className="certificat-scolarite-form-row">
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                            <select
                                name="prepositionLieuNaissance"
                                value={formData.prepositionLieuNaissance}
                                onChange={handleInputChange}
                                className="certificat-scolarite-input"
                                style={{ width: '80px', flexShrink: 0 }}
                            >
                                <option value="à">à</option>
                                <option value="au">au</option>
                                <option value="en">en</option>
                                <option value="aux">aux</option>
                            </select>
                            <input
                                type="text"
                                name="lieuNaissance"
                                value={formData.lieuNaissance}
                                onChange={handleInputChange}
                                placeholder="Lieu de naissance"
                                required
                                className="certificat-scolarite-input"
                                style={{ flex: 1 }}
                            />
                        </div>
                        <input
                            type="text"
                            name="pays"
                            value={formData.pays}
                            onChange={handleInputChange}
                            placeholder="Pays"
                            required
                            className="certificat-scolarite-input"
                        />
                    </div>

                    <div className="certificat-scolarite-form-row">
                        <input
                            type="text"
                            name="numeroEnregistrement"
                            value={formData.numeroEnregistrement}
                            onChange={handleInputChange}
                            placeholder="Numéro d'enregistrement"
                            required
                            className="certificat-scolarite-input"
                        />
                        <input
                            type="text"
                            name="anneeAcademique"
                            value={formData.anneeAcademique}
                            onChange={handleInputChange}
                            placeholder="Année académique (ex: 2024-2025)"
                            required
                            className="certificat-scolarite-input"
                        />
                    </div>

                    <div className="certificat-scolarite-form-row">
                        <textarea
                            name="sujetRecherche"
                            value={formData.sujetRecherche}
                            onChange={handleInputChange}
                            placeholder="Sujet de recherche"
                            required
                            className="certificat-scolarite-textarea"
                            rows="3"
                        />
                    </div>

                    <div className="certificat-scolarite-form-row">
                        <input
                            type="date"
                            name="dateEmission"
                            value={formData.dateEmission}
                            onChange={handleInputChange}
                            required
                            className="certificat-scolarite-input"
                        />
                    </div>

                    <button type="submit" className="certificat-scolarite-submit-btn">
                        {isEditing ? 'Modifier le Certificat' : 'Créer le Certificat'}
                    </button>
                    {isEditing && (
                        <button 
                            type="button" 
                            onClick={resetForm}
                            className="certificat-scolarite-submit-btn"
                            style={{ 
                                marginTop: '10px', 
                                background: 'linear-gradient(135deg, #6c757d, #5a6268)' 
                            }}
                        >
                            Annuler
                        </button>
                    )}
                </form>

                {/* Document List */}
                <div className="certificat-scolarite-document-list">
                    {documents.map((document) => (
                        <div key={document.id} className="certificat-scolarite-document-item">
                            <button
                                className="certificat-scolarite-document-toggle"
                                onClick={() => showDocumentModal(document)}
                            >
                                <Eye size={16} style={{ marginRight: '8px' }} />
                                {document.noms} - Certificat de Scolarité
                            </button>
                            <button
                                className="certificat-scolarite-document-toggle"
                                onClick={() => handleEditDocument(document)}
                                style={{ 
                                    marginRight: '8px', 
                                    background: '#ffc107',
                                    color: '#000'
                                }}
                            >
                                <Edit size={16} style={{ marginRight: '8px' }} />
                                Modifier
                            </button>
                            <Popconfirm
                                title="Supprimer le document"
                                description="Êtes-vous sûr de vouloir supprimer ce document ?"
                                onConfirm={() => handleDeleteDocument(document.id)}
                                okText="Oui"
                                cancelText="Non"
                                okButtonProps={{ danger: true }}
                            >
                                <button className="certificat-scolarite-delete-btn">
                                    <Delete size={15} />
                                </button>
                            </Popconfirm>
                        </div>
                    ))}
                </div>
            </div>

            {/* Modal */}
            <ConfigProvider>
                <Modal
                    title={<span style={{ color: 'white' }}>Aperçu - Certificat de Scolarité</span>}
                    open={modalVisible}
                    onCancel={handleModalClose}
                    width="95vw"
                    style={{ top: 10 }}
                    centered={false}
                    maskClosable={true}
                    keyboard={true}
                    footer={[
                        <Button
                            key="download"
                            type="primary"
                            icon={<Download size={16} />}
                            onClick={downloadPDF}
                            style={{
                                backgroundColor: '#28a745',
                                borderColor: '#28a745',
                                fontWeight: 'bold'
                            }}
                        >
                            Télécharger PDF
                        </Button>,
                        <Button
                            key="close"
                            onClick={handleModalClose}
                            style={{ fontWeight: 'bold' }}
                        >
                            Fermer
                        </Button>
                    ]}
                >
                    {selectedDocument && (
                        <div className="certificat-scolarite-modal-content">
                            <CertificatScolariteDocument document={selectedDocument} qrCodeData={qrCodeData} />
                        </div>
                    )}
                </Modal>
            </ConfigProvider>
        </div>
    );
};

// Document Component
const CertificatScolariteDocument = ({ document: docData, qrCodeData }) => {
    const formatDateWithMonthName = (dateStr) => {
        const date = new Date(dateStr);
        const months = [
            "janvier", "février", "mars", "avril", "mai", "juin",
            "juillet", "août", "septembre", "octobre", "novembre", "décembre"
        ];
        const day = date.getDate();
        const month = months[date.getMonth()];
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
    };

    const formatDateDDMMYYYY = (dateStr) => {
        const date = new Date(dateStr);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    return (
        <div id="certificat-scolarite-document" className="certificat-scolarite-document">
            {/* Header */}
            <div className="certificat-scolarite-header">
                <div className="certificat-scolarite-header-row">
                    <div className="certificat-scolarite-left-logo">
                        <img src={unilislogo} alt="Logo UNILIS" className="certificat-scolarite-logo" />
                        <div className="certificat-scolarite-left-text">
                            <h1 className="certificat-scolarite-school">ECOLE DOCTORALE</h1>
                            <h2 className="certificat-scolarite-subtitle">Cotutelle Internationale de Thèse et Co-diplomation</h2>
                        </div>
                    </div>
                    <div className="certificat-scolarite-header-center">
                        <h3 className="certificat-scolarite-university">UNIVERSITE DE LISALA</h3>
                        <p className="certificat-scolarite-center">Centre Interuniversitaire de Recherche Pluridisciplinaire (Cirep)</p>
                        <p className="certificat-scolarite-status">Status: Universite publique</p>
                        <p className="certificat-scolarite-web">Web: www.universitedelisala.ac.cd</p>
                        <p className="certificat-scolarite-email">Email: info@universitedelisala.ad.cd</p>
                    </div>
                    <div className="certificat-scolarite-right-logo">
                        <img src={edaplogo} alt="Logo EDAP" className="certificat-scolarite-logo" />
                    </div>
                </div>
            </div>

            {/* Black separator line */}
            <div className="certificat-scolarite-separator"></div>

            {/* Title */}
            <div className="certificat-scolarite-title-section">
                <h1 className="certificat-scolarite-main-title">CERTIFICAT DE SCOLARITE</h1>
            </div>

            {/* Document Body */}
            <div className="certificat-scolarite-document-body">
                <div className="certificat-scolarite-main-text">
                    <p>
                        Je soussigné Professeur Abbé TEBAKABE ALOMO Donat; Recteur de l'Université de Lisala atteste par la présente que <strong>{docData.noms}</strong>, né(e) le <strong>{formatDateDDMMYYYY(docData.dateNaissance)}</strong> {docData.prepositionLieuNaissance || 'à'} <strong>{docData.lieuNaissance}</strong>, <strong>{docData.pays}</strong>, enregistré(e) sous No <strong>{docData.numeroEnregistrement}</strong>;<br/><br/>
                        est régulièrement inscrit(e) au programme doctoral en cotutelle internationale et Codiplomation entre l'Université de Lisala en République Démocratique du Congo et UNCR University aux Etats Unis en Classe: MPhil, PhD I, PhD II, PhD III, HDR ; année académique: <strong>{docData.anneeAcademique}</strong><br/><br/>
                        Sujet de recherche : <strong>{docData.sujetRecherche}</strong><br/><br/>
                        Références de l'Université de Lisala :<br/><br/>
                        <strong>Statut de l'Université :</strong> Université Publique ou Université du Gouvernement de la RDC.<br/>
                        <strong>Adresse physique de l'Université:</strong> Avenue Aérodrome n° 20, Quartier 3C, Commune de Bolikango, Ville de Lisala, Province de la Mongala<br/>
                        <strong>Adresse électronique de l'Université :</strong> https://universitedelisala.ac.cd/<br/>
                        <strong>Adresse postale de l'Université :</strong> B.P 9289 Kinshasa I<br/>
                        <strong>International Ranking information :</strong> https://www.unirank.org/cd/uni/university-of-lisala/<br/>
                        <strong>Reconnaissance Internationale par UNESCO :</strong> https://www.whed.net/institutions/IAU-028487<br/>
                        <strong>Reconnaissance Internationale de ses diplômes :</strong> Les diplômes de Doctorat l'Université de Lisala reçoivent des équivalences au niveau mondial. Cas de World Education Services/ : https://www.wes.org/ . A titre d'Exemple Dr Meguem Edouidge (E-mail : esineng@yahoo.fr ) en fait un témoignage d'obtention de l'Equi-valence de World Éducation Services(WES), qui l'a permis de postuler à des emplois situés au Canada ou aux USA … sur la seule base de son PhD puisqu'il est déjà reconnu par le Service d'équivalence internationa-lement, organisme habilité .<br/><br/>
                        Le présent certificat lui est délivré pour servir et valoir ce que de droit.<br/><br/>
                        Lisala, le <strong>{formatDateDDMMYYYY(docData.dateEmission)}</strong>
                    </p>
                </div>
            </div>

            {/* Signature */}
            <div className="certificat-scolarite-signature">
                <div className="certificat-scolarite-signature-content">
                    <img src={rectorSig} alt="Signature du Recteur" className="certificat-scolarite-signature-image" />
                    <p className="certificat-scolarite-signature-name">Pr Abbé Donat TEBAKABE ALOMO</p>
                    <p className="certificat-scolarite-signature-title">Recteur et Coordinateur du CIREP</p>
                </div>
                {qrCodeData && (
                    <div className="certificat-scolarite-qr-section">
                        <img
                            src={qrCodeData}
                            alt="QR Code - Document Verification"
                            className="qr-code"
                            style={{
                                width: '60px',
                                height: '60px',
                                border: '1px solid #000'
                            }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default CertificatScolariteTemplate;

